/**
 * `Translation`, `PageResult` and `translated` are shared with every other
 * `/v1/merchant` module and now live in `lib/api/common`. They are re-exported
 * here so this feature's public surface is unchanged.
 */
export type { PageResult, Translation } from '@/lib/api/common'
export { translated } from '@/lib/api/common'

import type { Translation } from '@/lib/api/common'

export interface ProductImage {
  id: string
  url: string
  name: string
  type: string
}

/** A category as the product endpoints embed it — the picker reads the same shape. */
export interface ProductCategory {
  id: string
  name: Translation
  image: ProductImage | null
}

/**
 * A brand as the product endpoints embed it, and as the form's picker and the
 * list's filter read it.
 *
 * Declared here rather than imported from `features/brands`: that feature
 * imports this one for its cache keys, and an edge the other way would make
 * the two barrels circular — the same reason `OptionSetSummary` is.
 */
export interface ProductBrand {
  id: string
  name: Translation
  logo: ProductImage | null
  /** Sent on the brand list; an embedded brand may leave it out. */
  isActive?: boolean
}

export interface ChoiceGroupSummary {
  id: string
  name: Translation
  isRequire: boolean
  selectType: string | null
}

/**
 * A saved option axis — `Size: S, M, L` — as the form's picker reads it.
 *
 * Declared here rather than imported from `features/modifiers`: that feature already
 * imports this one for its cache keys, and a second edge the other way would make the
 * two barrels circular. Only the three fields the picker uses are spelled out.
 */
export interface OptionSetSummary {
  id: string
  name: Translation
  items: { id: string; name: Translation }[]
}

/**
 * What a product is. Stock is only counted for a `stockable` one; a `consumable`
 * is made to order and has no quantity to run out of. A `composite` is made from
 * a recipe — inventory gives a new one an empty draft recipe — and selling it
 * uses up its ingredients' stock rather than any of its own.
 */
export const ProductType = {
  Stockable: 'stockable',
  Consumable: 'consumable',
  Composite: 'composite',
} as const

export type ProductType = (typeof ProductType)[keyof typeof ProductType]

export const ProductUnit = {
  Unit: 'unit',
  Piece: 'piece',
  Kilogram: 'kilogram',
} as const

export type ProductUnit = (typeof ProductUnit)[keyof typeof ProductUnit]

/** The list filters, spelled the way the API spells them rather than the way we read them. */
export const ProductEnabledFilter = {
  Enabled: 'enable',
  Disabled: 'disable',
} as const

export type ProductEnabledFilter = (typeof ProductEnabledFilter)[keyof typeof ProductEnabledFilter]

export const ProductStatusFilter = {
  Published: 'published',
  Unpublished: 'unpublished',
} as const

export type ProductStatusFilter = (typeof ProductStatusFilter)[keyof typeof ProductStatusFilter]

/** One name/value pair pinning a variant to a point on an option axis. */
export interface ProductAttribute {
  name: string
  value: string
}

/** An option axis and the values it offers — `Size: S, M, L`. */
export interface ProductOption {
  name: string
  values: { name: string; image?: ProductImage | null }[]
}

/** An outlet as a list row names it — just enough for the Branches column. */
export interface ProductBranchSummary {
  id: string
  name: Translation
}

/** A row of the product list, and also the shape a variant comes back as. */
export interface ProductSummary {
  id: string
  name: Translation
  sku: string
  gtin: string | null
  unit: string | null
  type: string
  enabled: boolean
  published: boolean
  price: number
  salePrice: number
  /**
   * What one unit costs the business to make or buy, in USD. `null` is "not
   * known" — never read as zero, which would put every sale at a 100% margin.
   * Optional because a server a release behind does not send it; absent reads
   * the same as `null`.
   */
  cost?: number | null
  /**
   * The fixed amount the cashier who rang the sale earns per unit sold, in USD.
   * `null` is "none"; absent is a server that does not send it yet.
   */
  incentive?: number | null
  stockQuantity: number
  numberOfVariants: number
  thumbnail: ProductImage | null
  categories: ProductCategory[]
  /**
   * The product's brand. Sent for a parent row; a variant has its parent's and
   * arrives without one. `null` is "no brand", absent is "not sent".
   */
  brand?: ProductBrand | null
  productAttributes: ProductAttribute[] | null
  /**
   * The variants, sent only for a product that has them. A variant is loaded
   * without children of its own, so this nests exactly one level.
   */
  children?: ProductSummary[]
  /**
   * The outlets this product is carried at. The list sends it for a parent row;
   * a variant has its parent's and arrives without it. Absent means "not sent",
   * never "carried nowhere" — that is an empty array.
   */
  branches?: ProductBranchSummary[]
}

/** A choice group as it hangs off a product, carrying that product's own limits. */
export interface ProductChoice {
  id: string
  name: Translation
  isRequire: boolean
  minSelect: number
  maxSelect: number
  selectType: string | null
}

/** A branch as a product embeds it — an object carrying its id, or the bare id. */
export type ProductBranchRef = string | { id?: string; tenantBranchId?: string }

export interface ProductDetail {
  id: string
  name: Translation
  description: Translation | null
  slug: string
  sku: string
  gtin: string | null
  unit: string | null
  type: string
  enabled: boolean
  published: boolean
  price: number
  salePrice: number
  /** See `ProductSummary` — the same two, on the product itself. */
  cost?: number | null
  incentive?: number | null
  stockQuantity: number
  thumbnail: ProductImage | null
  images: ProductImage[]
  specsImages: ProductImage[]
  options: ProductOption[] | null
  categories: ProductCategory[]
  /**
   * The product's brand, embedded whole so the picker can show it even once it
   * has been deactivated and dropped out of the active list. `brandId` is read
   * too, in case the detail sends the bare id instead.
   */
  brand?: ProductBrand | null
  brandId?: string | null
  /**
   * The outlets this product is carried at. Only the ids are read back into the form.
   *
   * The detail endpoint's name for this is not pinned down, so the three plausible
   * spellings are all typed and `branchIdsOf` in the mapper reads whichever came.
   */
  tenantBranches?: ProductBranchRef[] | null
  branches?: ProductBranchRef[] | null
  tenantBranchIds?: string[] | null
  choices: ProductChoice[]
  /**
   * The variants. `PosProductDetailDto` does not map these itself — the service
   * attaches them after the fact — so they arrive as list rows, not detail rows.
   */
  children: ProductSummary[]
}

export interface ProductListQuery {
  limit: number
  offset: number
  searchText?: string
  categoryIds?: string[]
  /** Spelled like `categoryIds`, and dropped rather than sent when empty. */
  brandIds?: string[]
  enabled?: ProductEnabledFilter
  status?: ProductStatusFilter
  /**
   * Only products carried at this outlet.
   *
   * Optional, and a view the merchant chooses rather than a scope imposed on
   * them — the catalogue itself stays company-wide. A product's outlets are set
   * on the product form, so this genuinely narrows: a product set up for one
   * branch is absent from another's list.
   */
  branchId?: string
}

/** One variant on the way up. `id` is absent for a variant being added. */
export interface SaveProductVariant {
  id?: string
  sku: string
  gtin?: string
  price: number
  /** Three-valued, like the product's own — see `SaveProductBody`. */
  cost?: number | null
  incentive?: number | null
  /** The variant's picture, as the id of an already-uploaded file. Omitted when there is none. */
  thumbnailId?: string
  stockQuantity?: number
  productAttributes: ProductAttribute[]
}

export interface SaveProductChoiceGroup {
  choiceGroupId: string
  minSelect: number
  maxSelect: number
}

/**
 * The body `PUT /v1/merchant/products` takes. It creates when `id` is absent and updates
 * when it is present, which is why one shape covers both.
 *
 * The four id arrays are not optional on the server, and `productVariants` is read
 * without a guard — every one of them has to be sent even when empty.
 */
export interface SaveProductBody {
  id?: string
  name: Translation
  /**
   * The product's picture, as the id of an already-uploaded file.
   *
   * Left out rather than sent as `null` when there is none: the server runs the
   * whole body through `omitBy(isNil)`, so a nil thumbnail is dropped and the
   * column keeps whatever it held. Clearing a picture is not something this
   * endpoint can express — replacing one is.
   */
  thumbnailId?: string
  description?: Translation
  sku?: string
  gtin?: string
  unit?: string
  type: string
  price: number
  /**
   * Three-valued, like `brandId`: a number sets it, `null` clears it, and left
   * out the product keeps what it had. The form always sends both — `null` for
   * an empty box — so emptying the field is how a merchant takes a cost off.
   */
  cost?: number | null
  incentive?: number | null
  published: boolean
  categoryIds: string[]
  /**
   * Three-valued, deliberately. An id sets the brand; `null` takes it off; left
   * out, the product keeps whatever it had. `null` goes up only when the
   * merchant cleared a brand the product was saved with — see `brandIdForSave`.
   */
  brandId?: string | null
  specImageIds: string[]
  bannerImageIds: string[]
  tenantBranchIds: string[]
  options: ProductOption[]
  choiceGroups: SaveProductChoiceGroup[]
  productVariants: SaveProductVariant[]
}

/* -------------------------------------------------------------------------- */
/* Bulk branch links                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Which way a bulk branch write goes.
 *
 * Neither replaces a product's outlets: linking adds to what each product
 * already has, unlinking takes away. Three products carried at different
 * branches can all be put on a new outlet without being flattened to one list —
 * replacing is the product form's job.
 */
export const ProductBranchLinkMode = {
  Link: 'link',
  Unlink: 'unlink',
} as const

export type ProductBranchLinkMode =
  (typeof ProductBranchLinkMode)[keyof typeof ProductBranchLinkMode]

/** The body both directions take. Parent ids only — variants follow their parent. */
export interface ProductBranchLinkBody {
  productIds: string[]
  tenantBranchIds: string[]
}

/* -------------------------------------------------------------------------- */
/* Inventory link                                                              */
/* -------------------------------------------------------------------------- */

/**
 * One row of a product that inventory keeps as a stock item, and the id it is
 * known by there.
 *
 * A product sold in several variants is its variants — the parent only groups
 * them — and a product sold one way is itself. `kind` says which lookup finds
 * it: inventory files a variant under `variantId` and never under its parent.
 */
export interface ProductInventoryRef {
  id: string
  kind: 'product' | 'variant'
  /** How the merchant knows the row: the variant's values, or the product's name. */
  label: string
  sku: string
}

export interface ProductInventoryRowStatus {
  ref: ProductInventoryRef
  /** False when inventory answered 404: the save's message to it never took. */
  isTracked: boolean
}

/** What `POST /v1/merchant/products/:id/inventory/retry` answers, by row id. */
export interface ProductInventoryRetryResult {
  sent: string[]
  /** Rows with no SKU inventory takes (1 to 100 characters), so not sent. */
  skipped: string[]
}

/* -------------------------------------------------------------------------- */
/* Bulk import and export                                                      */
/* -------------------------------------------------------------------------- */

/** One thing wrong with one row, named by the row number the merchant sees in Excel. */
export interface ProductImportError {
  row: number
  productKey: string
  message: string
}

/**
 * What a validate or an import run came to.
 *
 * One shape for both, so the preview and the result are the same panel. `applied`
 * is the whole difference: false on a dry run, and false on a real import that
 * the errors stopped before it wrote anything.
 */
export interface ProductImportResult {
  applied: boolean
  /** Data rows read from the sheet, blank ones excluded. */
  rows: number
  /** Products those rows describe, variants folded into their parent. */
  products: number
  created: number
  updated: number
  errors: ProductImportError[]
}

/**
 * What the filter bar holds. One category rather than the array the API accepts —
 * the bar offers a single dropdown, and widening that is a UI change, not a type
 * change.
 */
export interface ProductFilterValues {
  searchText: string
  categoryId: string | null
  brandId: string | null
  enabled: ProductEnabledFilter | null
  status: ProductStatusFilter | null
}

export const emptyProductFilters: ProductFilterValues = {
  searchText: '',
  categoryId: null,
  brandId: null,
  enabled: null,
  status: null,
}
